import React from "react";
import {
  Lock,
  Search,
  Zap,
  TrendingUp,
  Target,
  ShieldCheck,
} from "lucide-react";
import MockupCelular from "./MockupCelular";

export default function SecaoIvo({ conteudo }) {
  const ivo = conteudo.ivo;
  if (!ivo) return null;

  return (
    <section
      id="ivo"
      className="relative py-12 md:py-28 px-4 md:px-6 overflow-hidden"
      data-testid="secao-ivo"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-green-500/[0.04] via-transparent to-blue-500/[0.04] pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-green-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto">
        {/* Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 rounded-full">
            <Lock className="w-3.5 h-3.5 text-green-400" />
            <span className="text-xs font-semibold text-green-400 uppercase tracking-wider">
              Incluso nos planos pagos
            </span>
          </div>
        </div>

        {/* Título */}
        <div className="text-center mb-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white tracking-tight">
            Todo plano pago vem com o{" "}
            <span className="bg-gradient-to-r from-green-400 to-emerald-300 bg-clip-text text-transparent">
              Ivo
            </span>
            , sua IA de gestão.
          </h2>
        </div>

        {/* Subtítulo */}
        <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
          <p className="text-lg md:text-xl text-zinc-400 leading-relaxed">
            Contratou o Evolzap, ganhou um assistente de IA particular pra
            consultar dados, executar tarefas e analisar seu negócio direto no
            WhatsApp.
          </p>
        </div>

        {/* Grid 2 colunas: mockup + (cards 2x2 + selo) */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Mockup celular */}
          <div className="hidden md:flex justify-center lg:justify-start">
            <MockupCelular conversa={ivo.conversa} />
          </div>

          {/* Coluna direita: 4 cards 2x2 + selo */}
          <div className="relative flex flex-col gap-4 lg:pt-52">
            {/* Robo mascote espiando por cima dos 2 cards de cima (lg+) */}
            <div
              aria-hidden="true"
              className="pointer-events-none hidden lg:block absolute left-1/2 -translate-x-1/2 top-0 w-[70%] max-w-[420px] z-10"
              style={{ aspectRatio: "1536 / 1024" }}
            >
              <img
                src="/ivo-espiando-aberto.webp"
                alt=""
                className="absolute inset-0 w-full h-full object-contain select-none"
                draggable="false"
              />
              <img
                src="/ivo-espiando-fechado.webp"
                alt=""
                className="absolute inset-0 w-full h-full object-contain select-none ivo-blink"
                draggable="false"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-4 bg-[#111111] border border-white/5 rounded-xl hover:border-green-500/30 transition-all">
                <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center mb-2">
                  <Search className="w-4 h-4 text-green-500" />
                </div>
                <h3 className="text-sm font-semibold text-white mb-1.5">
                  Consulta tempo real
                </h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Caixa, vendas, estoque, agendamentos, inadimplência. Pergunte
                  e tenha a resposta em segundos.
                </p>
              </div>

              <div className="p-4 bg-[#111111] border border-white/5 rounded-xl hover:border-green-500/30 transition-all">
                <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center mb-2">
                  <Zap className="w-4 h-4 text-green-500" />
                </div>
                <h3 className="text-sm font-semibold text-white mb-1.5">
                  Executa ações
                </h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Cobra cliente, lança despesa, monta orçamento, atualiza
                  status. Tudo com confirmação.
                </p>
              </div>

              <div className="p-4 bg-[#111111] border border-white/5 rounded-xl hover:border-green-500/30 transition-all">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center mb-2">
                  <TrendingUp className="w-4 h-4 text-blue-400" />
                </div>
                <h3 className="text-sm font-semibold text-white mb-1.5">
                  Analisa seu negócio
                </h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Receita vs despesa, margem, ticket médio, riscos. Não só
                  números — interpretação.
                </p>
              </div>

              <div className="p-4 bg-[#111111] border border-white/5 rounded-xl hover:border-green-500/30 transition-all">
                <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center mb-2">
                  <Target className="w-4 h-4 text-blue-400" />
                </div>
                <h3 className="text-sm font-semibold text-white mb-1.5">
                  Sugere ações no caixa
                </h3>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  Aponta o que cobrar primeiro, o que cortar, onde reativar
                  cliente. Com valor em reais.
                </p>
              </div>
            </div>

            {/* Selo de privacidade — agora dentro da coluna direita */}
            <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-green-500/[0.06] to-blue-500/[0.06] border border-green-500/20 rounded-2xl">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-green-500/20 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4 text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-semibold text-sm mb-1">
                  Acesso exclusivo do dono
                </h4>
                <p className="text-zinc-400 text-xs leading-relaxed">
                  O Ivo conversa só com você. Seus clientes continuam atendidos
                  pelo Evolzap normalmente — ninguém vê suas conversas com ele.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
